import { AuthenticationResult, IdTokenClaims, InteractionStatus } from '@azure/msal-browser';
import { environment } from 'src/environments/environment';

export type IdTokenClaimsWithPolicyId = IdTokenClaims & {
  acr?: string;
  tfp?: string;
};

/**
 * URI de retorno tras login. Nunca debe ser /logged-out: esa ruta es solo post-logout
 * y si CIAM/Azure la usa como redirect de login la UI muestra "Sesión cerrada" con sesión válida.
 */
export function resolveLoginRedirectUri(): string {
  const raw = (environment.msalConfig.auth.redirectUri || window.location.origin).trim();
  const withoutTrailingSlash = raw.replace(/\/+$/, '');
  const withoutLoggedOut = withoutTrailingSlash.replace(/\/logged-out$/i, '');
  return withoutLoggedOut || window.location.origin;
}

export function resolvePostLogoutRedirectUri(): string {
  return `${resolveLoginRedirectUri()}/logged-out`;
}

export function hasMsalAccount(
  accounts: ReadonlyArray<unknown> | null | undefined
): boolean {
  return (accounts?.length ?? 0) > 0;
}

/**
 * ACQUIRE_TOKEN_SUCCESS a veces no trae idTokenClaims en la raíz del payload;
 * leer también desde account evita excepciones que disparaban logout automático.
 */
export function resolveMsalIdTokenClaims(
  payload: AuthenticationResult | null | undefined
): IdTokenClaimsWithPolicyId | null {
  if (!payload) {
    return null;
  }

  const claims =
    payload.idTokenClaims ??
    payload.account?.idTokenClaims ??
    null;

  return claims as IdTokenClaimsWithPolicyId | null;
}

export function isB2cPolicy(
  idToken: IdTokenClaimsWithPolicyId | null | undefined,
  policyName: string | undefined
): boolean {
  if (!idToken || !policyName) {
    return false;
  }

  return idToken.acr === policyName || idToken.tfp === policyName;
}

/**
 * ACCOUNT_REMOVED con 0 cuentas durante login/redirect no debe limpiar la sesión de app
 * (el cache MSAL puede vaciarse un instante y eso no implica logout explícito).
 */
export function shouldClearAppSessionOnAccountsRemoved(
  accountCount: number,
  interactionStatus: InteractionStatus
): boolean {
  if (accountCount > 0) {
    return false;
  }

  if (
    interactionStatus !== InteractionStatus.None &&
    interactionStatus !== InteractionStatus.Logout
  ) {
    return false;
  }

  return true;
}
